import { notify } from 'messages';
import { toError } from 'helpers/toError';
import type { Received } from 'validation';

/**
 * Resolve the record a dialog is about to open on, or say why it cannot open
 * and resolve to `undefined`.
 *
 * This is the counterpart of `EditDialog`'s load-error `Alert` for the dialogs
 * that are **not yet on `EditModel`**. Those have no shell to render an error
 * into, so the wording has to live somewhere, and five hand-written copies of a
 * three-outcome branch is how the defect in issue #1035 happened in the first
 * place. As each of these dialogs migrates to `EditModel` its call here becomes
 * four lines shorter and this helper loses a consumer; when it loses the last
 * one, delete it.
 *
 * The sentences differ because the useful action differs, which is the whole
 * point:
 *
 * - the record is gone → refreshing *will* help;
 * - the response was unreadable → refreshing will not help, and the operator is
 *   not the person who can fix it;
 * - part of it was unreadable → a viewer could show what arrived, but an editor
 *   cannot: saving writes the whole record back, so a field we mis-read is a
 *   field this dialog would overwrite with our substitute (#1220 decision 1);
 * - the request failed → the server said something, so repeat what it said.
 *
 * @param entityName Title-Case-As-Displayed, as `EditDialog` takes it; the
 *   sentences lowercase it.
 */
export const recordToEdit = async <T>(entityName: string, load: () => Promise<Received<T | null>>): Promise<T | undefined> => {
  const name = entityName.toLowerCase();
  let answer: Received<T | null>;
  try {
    answer = await load();
  } catch (err) {
    notify.error(`Could not open the ${name}: ${toError(err).message}`);
    return undefined;
  }
  if (answer.result === 'unreadable') {
    notify.error(`Could not open the ${name}: the server's response could not be read. Please report this.`);
    return undefined;
  }
  if (answer.result === 'degraded') {
    notify.error(`Could not open the ${name}: part of the server's response could not be read, so saving it could overwrite that part. Please report this.`);
    return undefined;
  }
  const record = answer.value;
  if (record === null) {
    notify.error(`This ${name} no longer exists; the UI may be out of date. Please refresh.`);
    return undefined;
  }
  return record;
};
